import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { EumEditor } from '@appbase/components/editor'
import { FlowFileMeta } from '@appbase/components/common/FlowFileUploader/flow-file-meta'
import { invalidateUrl } from '@appbase/util/invalidate-url'
import { readablizeBytes } from '@appbase/util/size-helper'
import { getGUID } from '@appbase/util/guid'
import localStorageHelper from '@appbase/util/local-storage-helper'
import flowHelper from '@appbase/components/common/FlowFileUploader/flow-helper'
import EumFileUpload from 'eum-file-upload'
import api from '@board/api'
import { mapGetters } from 'vuex'
import * as accountTypes from '@account/store/types'
import * as dateutil from '@appbase/util/date-helper'

const enum WriteMode {
    Write = 0,
    Edit = 1,
    Reply = 2,
}

@Component({
    components: { 
        EumEditor,
        EumFileUpload
    },
    computed: {
        ...mapGetters({
            user: accountTypes.USER,
        })
    },
})
export default class WriteContainer extends Vue {
    //
    // ──────────────────────────────────────────────── I ──────────
    //   :::::: D A T A : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────
    //

    tinyOptions: any = {
        content_style: `
                    p { font-family: dotum; }
                    body {font-family: dotum; font-size: 10pt;}
                    body#tinymce p {
                        font-family: dotum; 
                        margin-top: 0.3em;
                        margin-bottom: 0.3em;
                    }`,
        height: 500,
        inline: false,
        menubar: false,
        language: 'ko_KR',
    }

    writeConfig: board.WriteConfig | null = null
    contentFields: board.ContentField[] = []
    returnValues: common.WriteComponentReturnSet[] = []
    uploaderConfig: FlowFileMeta = {
        code: '',
        codeValues: [],
        type: 'file',
    }

    fieldLoaded: boolean = false
    isLoading: boolean = false
    isFileUploading: boolean = false
    currentBoard: number = 0
    currentArticle: number = 0
    currentArticleModel: article.ArticleDetail | null = null
    parentArticle: number = 0
    writeMode: WriteMode = WriteMode.Write

    mUsePreface: boolean = false
    mUseNotice: boolean = false
    mUseAttachment: boolean = true
    mUseTags: boolean = false
    mUseNameless: boolean = false
    mUseContent: boolean = false

    mBoardName: string = ''
    mIsNotice: string = 'none'
    mTitle: string = ''
    mContent: string = ''
    mTags: string[] = []
    mFileCode: string = ''
    mChildPreface: string | null = null
    mParentPreface: string | null = null
    mFileSizeDesc: string = ''
    mWritePassword: string = ''

    mSecret: boolean = false

    mNoticeStartDate: Date | null = null
    mNoticeEndDate: Date | null = null

    childPrefaces: board.BaordPreface[] = []
    parentPrefaces: board.BoardPreface[] = []
    mShowChildPreface: boolean = false

    uploadUrl: string = ''
    deleteUrl: string = ''
    sizeLimit: number | null = null
    bannedExtentions: string[] = []
    files: any[] = []
    totalSize: number = 0

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: G E T T E R S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //

    user!: account.User

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: M E T H O D S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //

    getWriteConfig(boardId: number) {
        api.board.getWriteConfig(boardId).then((res) => {
            this.writeConfig = res
            this.mBoardName = this.writeConfig.boardName
            this.mUsePreface = this.writeConfig.usePreface
            this.mUseNotice = this.writeConfig.usePrimaryNotice
            this.mUseAttachment = this.writeConfig.useAttachment
            this.mUseTags = this.writeConfig.useTag
            this.mUseContent = this.writeConfig.useContent
            this.mUseNameless = this.writeConfig.useNameless
            //this.contentFields = this.writeConfig.contentFields.filter((f) => ids.contentFieldFilter(f))
            this.uploaderConfig.codeValues = ['board', this.user.comCode, dateutil.uGetDateFormat('yyyyMM', new Date()), this.writeConfig.boardId.toString()]
            this.uploadUrl = invalidateUrl('/cm/file/store/flow')
            this.deleteUrl = invalidateUrl('/cm/file/store/deleteFile')          

            if(this.writeMode == WriteMode.Edit) {
                //this.checkRightNameless(this.writeConfig).then(checkRes => {
                //    if(checkRes) {
                        var editloadPromise: Promise<any>[] = [
                            api.article.getArticleDetail(this.currentBoard, this.currentArticle),
                            api.article.getSystemAuth(this.currentBoard)
                        ]

                        Promise.all(editloadPromise)
                            .then(async editload => {                            
                                this.currentArticleModel = editload[0];
                                this.mIsNotice = editload[0].isNotice ? "notice" : "none";
                                if(!res.useNameless && !this.checkEditRights(editload[0].createdUser, editload[1])) {
                                    await(window as any).alertAsync('수정권한이 없습니다.');
                                    this.isloading(false)
                                    history.back()
                                } else {
                                    this.mountCallback(res);
                                }                       
                            });
                //    }
                //});         
            } else {
                this.mountCallback(res);
            }
        
            if (this.mUseAttachment) 
                this.$emit('useAttachment')
        })
    }

    checkEditRights(createdUser: string, systemAuths: article.SystemAuth[]) {
        if(createdUser === this.user.personCode)
            return true;

        if(systemAuths.findIndex(a => a.update || a.isMenuAdmin) >= 0)
            return true;

        return false;
    }

    mountCallback(config: board.WriteConfig) {
        if (config.attachableExtension && config.attachableExtension != null) {
            var bannedExtensions = config.attachableExtension.split(';')
            this.bannedExtentions = bannedExtensions
        }

        if (config.attachmentSizeLimit && config.attachmentSizeLimit != null) {
            this.sizeLimit = config.attachmentSizeLimit * 1024 * 1024
            this.mFileSizeDesc = `( 0 / ${readablizeBytes(this.sizeLimit)})`
        } else {
            this.sizeLimit = null
        }

        //this.mountPrefaces(config);
        this.fillComponentValues(this.currentArticleModel);
        this.fieldLoaded = true;
        this.isloading(false)
    }

    mountPrefaces(config: board.WriteConfig) {
        if (!config.usePreface) return

        var targetPreface: string | null = null

        if (this.writeMode == WriteMode.Reply) {
            var replyPreface = localStorageHelper.get(`$board/${this.parentArticle.toString()}/preface`)
            targetPreface = replyPreface == null ? null : replyPreface.trim()
        } else if (this.writeMode == WriteMode.Edit && this.currentArticleModel != null) {
           targetPreface = this.currentArticleModel.prefaceId
        }

        var serverPrefaces = config.prefaces
        var serverChilds = serverPrefaces.filter((p) => p.parentPrefaceId != null)
        var serverParents = serverPrefaces.filter((p) => p.parentPrefaceId == null)

        if (serverChilds.length > 0) {
            this.mShowChildPreface = true

            if (targetPreface == null) {
                this.parentPrefaces = serverParents
                this.mParentPreface = serverParents[0].prefaceId
                this.childPrefaces = serverChilds.filter((c) => c.parentPrefaceId === this.mParentPreface)
                this.mChildPreface = this.childPrefaces[0].prefaceId
            } else {
                var tpmodel = serverChilds.find((c) => c.prefaceId === targetPreface?.trim())

                if (tpmodel) {
                    this.parentPrefaces = serverParents
                    this.mParentPreface = tpmodel.parentPrefaceId
                    this.childPrefaces = serverChilds.filter((c) => c.parentPrefaceId === this.mParentPreface)
                    this.mChildPreface = tpmodel.prefaceId
                } else {
                    this.parentPrefaces = serverParents
                    this.mParentPreface = serverParents[0].prefaceId
                    this.childPrefaces = serverChilds.filter((c) => c.parentPrefaceId === this.mParentPreface)
                    this.mChildPreface = this.childPrefaces[0].prefaceId
                }
            }
        } else {
            this.parentPrefaces = serverParents
            this.mParentPreface =
                targetPreface == null ? this.parentPrefaces[0].prefaceId : targetPreface.trim()

            console.log(this.mParentPreface)
        }
    }

    fillComponentValues(article: article.ArticleDetail | null) {
        if (article == null) {
            this.mFileCode = getGUID(false)
            this.uploaderConfig.code = this.mFileCode
            this.contentFields.forEach((c) => {
                this.returnValues.push({
                    fieldId: c.fieldId,
                    returnValue: null,
                })
            })
        } else {
            this.mTitle = article.title
            this.mContent = article.content
            this.mFileCode = article.fileCode || getGUID(false)
            this.mTags = article.tags == null ? [] : article.tags.split(',').filter((t) => t != null && t.trim().length > 0)
            this.mSecret = article.isSecret ?? false
            this.mNoticeStartDate = article.noticeStartDate
            this.mNoticeEndDate = article.noticeEndDate
            this.uploaderConfig.code = this.mFileCode
            api.article.getArticleFile(this.mFileCode).then((res: any) => {
                this.files = res.map(
                    (m: any) => flowHelper.convertToFlowFile({
                            ...m,
                            uniqueIdentifier: m.serverPath,
                            type: 'Database'
                    })
                )
                this.updateSizeDesc()
            })

            this.contentFields.forEach((c) => {
                var find = article.fieldValues.find((f) => f.fieldId == c.fieldId)
                var passVal = find?.value

                this.returnValues.push({
                    fieldId: c.fieldId,
                    returnValue: passVal ? passVal : null,
                })
            })
        }
    }

    fileUploadStatusChaged(data: any) {
        if (data === 'uploading') {
            this.isFileUploading = true
        } else {
            this.isFileUploading = false
        }
    }

    fileItemsChanged(files: any[]) {
        if (files == null || files.length == 0) {
            this.updateSizeDesc()
        } else {
            if (this.writeMode == WriteMode.Edit && this.mFileCode != null) {

                api.article.createArticleFile({
                    articleId: this.currentArticle,
                    boardId: this.currentBoard,
                    fileCode: this.mFileCode,
                    files: files.map(
                        (f) =>
                            <article.FileItem>{
                                name: f.name,
                                size: f.size,
                            }
                    ),
                })
            }

            this.updateSizeDesc()
        }
    }

    updateSizeDesc() {
        if (this.files == null || this.files.length == 0) {
            if (this.sizeLimit != null) {
                this.mFileSizeDesc = `( 0 / ${readablizeBytes(this.sizeLimit)})`
            } else {
                this.mFileSizeDesc = ''
            }
        } else {
            var sizeTotal = this.files.reduce((p, c, i) => p + c.size, 0)

            if (this.sizeLimit == null) {
                this.mFileSizeDesc = `( ${readablizeBytes(sizeTotal)})`
            } else {
                this.mFileSizeDesc = `( ${readablizeBytes(sizeTotal)} / ${readablizeBytes(this.sizeLimit)})`
            }
        }
    }

    loadEditorContent() {
        const editor: editor.IEditorProxy = this.$refs.editor as any 
        this.mContent = editor.getEditor().getBodyValue();
    }

    getRequestModel(draft: boolean = false) {
        if (this.writeConfig == null) return
        this.loadEditorContent();

        var fieldValues: article.FieldValueSet[] = []
        //var imageSrc = this.getImageSrc()
        var prefaceId = this.mShowChildPreface ? this.mChildPreface : this.mParentPreface

        this.returnValues.forEach((r) => {
            var returnValue = r.returnValue == null ? '' : r.returnValue.toString()
            fieldValues.push({
                fieldId: r.fieldId,
                value: returnValue,
            })
        })

        return <article.ArticleQuerySet>{
            fieldValues: fieldValues,
            isNotice: this.mIsNotice === 'notice',
            boardId: this.currentBoard,
            boardName: this.writeConfig.boardName,
            title: this.mTitle,
            content: this.mContent,
            tags: this.mTags.join(','),
            user: this.mUseNameless
                ? null
                : {
                    userCode: this.user.id,
                    userName: this.user.displayName,
                    userName_En: this.user.displayNameEng,
                    deptName: this.user.deptName,
                    deptName_En: this.user.deptNameEn,
                    userJobTitle: this.user.titleName,
                    userJobTitle_En: this.user.titleNameEn,
                    userDept: this.user.deptCode,
                    userMail: this.user.email,
                    userPhone: `${this.user.officeTel}/${this.user.mobileTel}`,
                },
            fileCode: this.files.length == 0 ? null : this.mFileCode,
            hasAttachFile: this.files.length == 0 ? false : true,
            hasImage: false,//imageSrc != null ? true : false,
            prefaceId: prefaceId,
            parentArticle: this.writeMode == WriteMode.Reply ? this.parentArticle : null,
            replyLevel: 0,
            imageFilePath: null,//imageSrc,
            isDraft: draft,
            isMailSend: this.writeConfig.useMailSend,
            password: this.mUseNameless ? this.mWritePassword : null,
            etC1: null,
            etC2: null,
            etC3: null,
            etC4: null,
            etC5: null,
            articleVersion: (this.currentArticleModel && this.currentArticleModel.articleVersion ? this.currentArticleModel.articleVersion : 1),
            isSecret: this.mSecret ?? false,
            noticeStartDate: this.mIsNotice === 'notice' ? this.mNoticeStartDate : null,
            noticeEndDate: this.mIsNotice === 'notice' ? this.mNoticeEndDate : null
        }
    }

    async checkValidate(article: article.ArticleQuerySet) {
        if (this.mTitle == null || this.mTitle.length == 0) {
            await(window as any).alertAsync(this.$t('M_BB_TitleIsNull').toString())
            return false
        }

        if(this.mUseNameless && this.mWritePassword.trim().length == 0) {
            await(window as any).alertAsync(this.$t('글수정_패스워드_입력').toString());
            return false;
        }

        if (this.isFileUploading) {
            await(window as any).alertAsync(this.$t('파일_업로드중').toString())
            return false
        }

        return true
    }
    
    createArticle(articleSet: article.ArticleQuerySet): Promise<common.CreateResultSet> {
        return api.article.createArticle(articleSet)
    }

    updateArticle(articleSet: article.ArticleQuerySet): Promise<common.UpdateResultSet> {
        return api.article.updateArticle(articleSet)
    }

    async write() {
        var queryModel = this.getRequestModel(false)

        if (queryModel && await this.checkValidate(queryModel)) {

            var requestPromise: Promise<any> = Promise.resolve()

            if (this.writeMode == WriteMode.Write || this.writeMode == WriteMode.Reply) {
                requestPromise = this.createArticle(queryModel);
            } else {
                queryModel.articleId = this.currentArticle
                requestPromise = this.updateArticle(queryModel)
            }

            requestPromise.then((res) => {
                this.close()
            }).catch(err => { console.log(err) });
        }
    }

    close() {
        this.$router.push({ path: `/board/list?id=${this.currentBoard}` })
    }

    attach() {
        (this.$refs.browseButton as any).click();
    }

    //
    // ────────────────────────────────────────────────────────── I ──────────
    //   :::::: L I F E C Y C L E : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────────
    //

    created() {
        this.isloading(true)
        var { boardId, articleId } = this.$route.query

        if (boardId) this.currentBoard = Number(boardId)

        if (articleId) {
            this.currentArticle = Number(articleId)
            this.writeMode = WriteMode.Edit
        }

        this.getWriteConfig(this.currentBoard)
    }
}
